/**
 * /well-servicing/wells/:id — one well
 * (GET / PATCH / DELETE /well-servicing/wells/:id, fieldiq-api#1269).
 *
 * Panels stack full width: details on top, the edit form below them when
 * editing (never a modal).
 *
 * Edit is WS_WELL_WRITE (admin, office_staff, supervisor); delete is WS_ADMIN
 * (admin, office_staff) and asks in-app first. Any other role reads. A
 * refusal that still arrives is shown as a refusal in the form, never as a
 * green toast; a 409 duplicate_well names the existing well and links to it.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, Droplet, MapPin, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { useDeleteWsWell, useUpdateWsWell, useWsCustomers, useWsMeta, useWsWell } from '../../hooks/useWellServicing';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import {
  canAdminWs,
  canWriteWsWells,
  catalogLabel,
  duplicateWell,
  fmtWsNumber,
  wellIdLabel,
  wellIdOf,
  withUnit,
  wsRefusal,
} from '../../lib/well-servicing';
import { useBackToList } from '../downhole/recordPage';
import { WellForm } from './WellForm';
import { WellStringPanel } from './WellString';
import { isNotFound, WS_BAND_SECONDARY, WS_EYEBROW, WS_PRIMARY, WS_SECONDARY, WS_SOUR_SKIN, WsBadge, WsDialog, WsEmpty, WsFormError, WsLoading, WsPanel, WsSpec } from './WsBits';

export default function WsWellDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const back = useBackToList('/well-servicing/wells');
  const { user } = useAuth();
  const query = useWsWell(id);
  const catalog = useWsMeta().data?.data ?? null;
  const canEdit = canWriteWsWells(user?.role);
  const canDelete = canAdminWs(user?.role);
  const customers = useWsCustomers(canEdit);
  const update = useUpdateWsWell();
  const del = useDeleteWsWell();
  const [editing, setEditing] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [duplicateOf, setDuplicateOf] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // The duplicate link opens another well on this same route: start it closed.
  useEffect(() => {
    setEditing(false);
    setEditError(null);
    setDuplicateOf(null);
    setDeleting(false);
  }, [id]);

  const well = query.data?.data ?? null;
  const meta = query.data?.meta ?? null;
  const failure = queryLoadFailure(query);
  const notFound = isNotFound(queryFailureError(query));
  const fluid = well ? well.fluidTypeLabel ?? catalogLabel(catalog?.fluidTypes, well.fluidType) : '—';
  const idLabel = wellIdLabel(meta);

  return (
    <div data-testid="ws-well-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={WS_EYEBROW}
        title={well?.wellName || (query.isLoading ? 'Well' : '—')}
        onBack={back}
        backLabel="Back"
        actions={
          well && (canEdit || canDelete) ? (
            <div className="flex flex-wrap gap-2">
              {canEdit && (
                <button
                  type="button"
                  className="fi-action fi-action--standard fi-action--primary"
                  onClick={() => {
                    setEditError(null);
                    setDuplicateOf(null);
                    setEditing(true);
                  }}
                  disabled={editing}
                >
                  <Pencil className="size-4" aria-hidden="true" />
                  Edit well
                </button>
              )}
              {canDelete && (
                <button type="button" className={WS_BAND_SECONDARY} onClick={() => { setDeleteError(null); setDeleting(true); }}>
                  <Trash2 className="size-4" aria-hidden="true" />
                  Delete
                </button>
              )}
            </div>
          ) : undefined
        }
        stats={[
          { icon: <Droplet className="size-4" aria-hidden="true" />, label: 'Fluid', tone: 'neutral', loading: query.isLoading, value: fluid },
          {
            icon: <AlertTriangle className="size-4" aria-hidden="true" />,
            label: 'Service',
            tone: well?.sour ? 'accent' : 'neutral',
            loading: query.isLoading,
            value: well ? (well.sour ? 'Sour' : 'Sweet') : '—',
          },
          { icon: <MapPin className="size-4" aria-hidden="true" />, label: 'Region', tone: 'neutral', loading: query.isLoading, value: well?.provinceState || '—' },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {query.isLoading ? (
            <WsLoading label="Loading well" />
          ) : notFound || (!failure && !well) ? (
            <WsPanel eyebrow="Well" title="Well">
              <WsEmpty>This well was not found. It may have been deleted.</WsEmpty>
            </WsPanel>
          ) : failure || !well ? (
            <WsPanel eyebrow="Well" title="Well">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="this well" />
            </WsPanel>
          ) : (
            <>
              <WsPanel eyebrow="Well" title="Details" testId="ws-well-details">
                <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
                  <WsSpec label="Customer" value={well.customerName || '—'} />
                  <WsSpec label={idLabel} value={wellIdOf(well, meta) || '—'} mono />
                  {/* The other country's identifier, only when this well holds one. */}
                  {idLabel === 'UWI' && well.apiNumber && <WsSpec label="API number" value={well.apiNumber} mono />}
                  {idLabel === 'API number' && well.uwi && <WsSpec label="UWI" value={well.uwi} mono />}
                  <WsSpec label="Licence number" value={well.licenceNumber || '—'} mono />
                  <WsSpec label="Surface location" value={well.surfaceLocation || '—'} />
                  <WsSpec label="Province / state" value={well.provinceName || well.provinceState || '—'} />
                  <WsSpec
                    label="Coordinates"
                    value={well.latitude != null && well.longitude != null ? `${fmtWsNumber(well.latitude)}, ${fmtWsNumber(well.longitude)}` : '—'}
                    mono
                  />
                  <WsSpec label="Field" value={well.fieldName || '—'} />
                  <WsSpec label="Pool" value={well.poolName || '—'} />
                  <WsSpec label="Fluid" value={fluid} />
                  <WsSpec label="Service" value={well.sour ? <WsBadge skin={WS_SOUR_SKIN} value="sour" /> : 'Sweet'} />
                  <WsSpec label="Expected H2S" value={withUnit(well.expectedH2sPpm, 'ppm')} mono />
                </dl>
                {well.notes && <p className="border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">{well.notes}</p>}
              </WsPanel>

              {editing && canEdit && (
                <WsPanel eyebrow="Edit" title="Edit well" testId="ws-well-edit">
                  <WellForm
                    key={well.id}
                    well={well}
                    catalog={catalog}
                    meta={meta}
                    customers={customers.data ?? []}
                    pending={update.isPending}
                    error={editError}
                    duplicateOf={duplicateOf}
                    submitLabel="Save well"
                    onCancel={() => setEditing(false)}
                    onSubmit={(body) => {
                      setEditError(null);
                      setDuplicateOf(null);
                      update.mutate(
                        { id: well.id, body },
                        {
                          onSuccess: () => {
                            toast.success('Well saved');
                            setEditing(false);
                          },
                          onError: (err) => {
                            const dup = duplicateWell(err);
                            setEditError(dup ? dup.message : wsRefusal(err, 'The well could not be saved.'));
                            setDuplicateOf(dup?.existingWellId ?? null);
                          },
                        },
                      );
                    }}
                  />
                </WsPanel>
              )}

              <WellStringPanel wellId={well.id} today={meta?.today ?? null} />
            </>
          )}
        </div>
      </section>

      {well && canDelete && (
        <WsDialog
          open={deleting}
          title="Delete well"
          onClose={() => setDeleting(false)}
          footer={
            <>
              <button type="button" className={WS_SECONDARY} onClick={() => setDeleting(false)} disabled={del.isPending}>
                Keep well
              </button>
              <button
                type="button"
                className={WS_PRIMARY}
                disabled={del.isPending}
                onClick={() =>
                  del.mutate(well.id, {
                    onSuccess: () => {
                      toast.success('Well deleted');
                      navigate('/well-servicing/wells', { replace: true });
                    },
                    onError: (err) => setDeleteError(wsRefusal(err, 'The well could not be deleted.')),
                  })
                }
              >
                {del.isPending ? 'Deleting…' : 'Delete well'}
              </button>
            </>
          }
        >
          <WsFormError message={deleteError} />
          <p className="text-sm text-fieldiq-text">
            Removes {well.wellName} from the register.
            {wellIdOf(well, meta) ? ` Its ${idLabel} is freed for another well.` : ''}
          </p>
        </WsDialog>
      )}
    </div>
  );
}
